import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import type { GLTF } from 'three-stdlib';
// Type-only three import — erased at build, so it does NOT pull three into the
// always-loaded bundle (this provider is mounted in Layout; Lazy3D boundary).
import type { Object3D, Mesh } from 'three';
import {
  addManualFeature,
  createManualSession,
  deleteManualFeature,
  type ManualFeatureInput,
  type ManualStoredFeature,
  type ManualTree,
} from '@/services/manualCad';
import { ManualCadContext } from './manual-cad-context';

// Release the GPU resources of a Tip GLTF we're about to drop. parseStl mints a
// fresh BufferGeometry + MeshStandardMaterial per recompute, and R3F's
// <primitive> never disposes an externally-owned object — so without this a long
// manual session leaks a geometry+material per edit → eventual context loss.
function disposeGltf(g: GLTF | null) {
  g?.scene.traverse((o: Object3D) => {
    const mesh = o as Mesh;
    mesh.geometry?.dispose?.();
    const mat = mesh.material;
    if (Array.isArray(mat)) mat.forEach((m) => m?.dispose?.());
    else mat?.dispose?.();
  });
}

export function ManualCadProvider({ children }: { children: ReactNode }) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [features, setFeatures] = useState<ManualStoredFeature[]>([]);
  const [tipId, setTipId] = useState<string | null>(null);
  const [tipGltf, setTipGltf] = useState<GLTF | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep the id in a ref too so addFeature can chain create→add without waiting
  // for the setState to flush.
  const sessionIdRef = useRef<string | null>(null);
  // Synchronous re-entrancy guard: `busy` state disables the buttons a frame
  // LATE, so a fast double-click could otherwise start two mutations at once
  // (two create() calls → a dropped feature, or out-of-order responses → stale
  // state). This ref blocks the second call immediately, serializing mutations.
  const busyRef = useRef(false);
  // Track the currently-rendered Tip GLTF so we can dispose it on replace/reset
  // /unmount without a stale-closure dep on the `tipGltf` state.
  const tipGltfRef = useRef<GLTF | null>(null);

  const setTip = useCallback((g: GLTF | null) => {
    if (tipGltfRef.current !== g) disposeGltf(tipGltfRef.current);
    tipGltfRef.current = g;
    setTipGltf(g);
  }, []);

  // Fold a server response into local state (tree + Tip mesh). The STL→GLTF
  // conversion is dynamically imported so three.js/STLLoader stay OUT of the
  // always-loaded bundle — this provider is mounted in Layout (Lazy3D boundary).
  const applyTree = useCallback(
    async (tree: ManualTree) => {
      setSessionId(tree.sessionId);
      sessionIdRef.current = tree.sessionId;
      const feats = tree.features ?? [];
      setFeatures(feats);
      setTipId(tree.tipId ?? null);
      setError(tree.mesh?.error ?? null);
      if (tree.mesh?.stl_b64) {
        // Fresh solid → render it (disposing the previous one).
        const { stlB64ToGltf } = await import('@/utils/manualCadMeshUtils');
        setTip(stlB64ToGltf(tree.mesh.stl_b64));
      } else if (!feats.some((f) => f.type === 'Pad')) {
        // No solid-producing feature left (sketch-only, or the last Pad was
        // deleted) → clear the viewport. When a solid DOES remain but this step
        // returned no mesh (e.g. adding a bare Sketch), keep the last render.
        setTip(null);
      }
    },
    [setTip],
  );

  const ensureSession = useCallback(async (): Promise<string> => {
    if (sessionIdRef.current) return sessionIdRef.current;
    const tree = await createManualSession();
    sessionIdRef.current = tree.sessionId;
    setSessionId(tree.sessionId);
    return tree.sessionId;
  }, []);

  const addFeature = useCallback(
    async (feature: ManualFeatureInput) => {
      if (busyRef.current) return; // serialize — drop a re-entrant click
      busyRef.current = true;
      setBusy(true);
      setError(null);
      try {
        const sid = await ensureSession();
        await applyTree(await addManualFeature(sid, feature));
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        busyRef.current = false;
        setBusy(false);
      }
    },
    [ensureSession, applyTree],
  );

  const deleteFeature = useCallback(
    async (featureId: string) => {
      const sid = sessionIdRef.current;
      if (!sid || busyRef.current) return;
      busyRef.current = true;
      setBusy(true);
      setError(null);
      try {
        await applyTree(await deleteManualFeature(sid, featureId));
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        busyRef.current = false;
        setBusy(false);
      }
    },
    [applyTree],
  );

  const reset = useCallback(() => {
    sessionIdRef.current = null;
    setSessionId(null);
    setFeatures([]);
    setTipId(null);
    setTip(null); // disposes the current Tip GLTF
    setError(null);
  }, [setTip]);

  // Dispose the last Tip GLTF when the provider unmounts (leaving the app /
  // logging out) so it doesn't linger on the GPU.
  useEffect(() => {
    return () => disposeGltf(tipGltfRef.current);
  }, []);

  const value = useMemo(
    () => ({
      sessionId,
      features,
      tipId,
      tipGltf,
      busy,
      error,
      addFeature,
      deleteFeature,
      reset,
    }),
    [
      sessionId,
      features,
      tipId,
      tipGltf,
      busy,
      error,
      addFeature,
      deleteFeature,
      reset,
    ],
  );

  return (
    <ManualCadContext.Provider value={value}>
      {children}
    </ManualCadContext.Provider>
  );
}
